import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@shared/ui/dialog";
import { Switch } from "@shared/ui/switch";
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import { ChevronUp, ChevronDown } from "lucide-react";

type EstimationRule = {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  priority: number;
};

interface EstimationRulesDialogProps {
  open: boolean;
  onClose: () => void;
}

export const EstimationRulesDialog = ({ open, onClose }: EstimationRulesDialogProps) => {
  
  // Example estimation rules with priority
  const [estimationRules, setEstimationRules] = useState<EstimationRule[]>([
    {
      id: "historical-average",
      name: "Historical Average",
      description: "Estimate based on average consumption from previous periods.",
      enabled: true,
      priority: 1
    },
    {
      id: "seasonal-adjustment",
      name: "Seasonal Adjustment",
      description: "Adjust estimation based on seasonal consumption patterns.",
      enabled: true,
      priority: 2
    },
    {
      id: "similar-customer",
      name: "Similar Customer Profile",
      description: "Estimate using data from customers with similar profiles.",
      enabled: false,
      priority: 3
    },
    {
      id: "fixed-value",
      name: "Fixed Value",
      description: "Use a predetermined fixed value for estimation.",
      enabled: true,
      priority: 4
    },
    {
      id: "last-reading-copy",
      name: "Last Reading Copy",
      description: "Copy the last valid reading as the estimate.",
      enabled: true,
      priority: 5
    }
  ]);

  const handleToggle = (ruleId: string) => {
    setEstimationRules(prevRules => 
      prevRules.map(rule => 
        rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
      )
    );
    
    const rule = estimationRules.find(r => r.id === ruleId);
    if (rule) {
    //   toast({
    //     title: `${rule.name} ${rule.enabled ? 'Disabled' : 'Enabled'}`,
    //     description: `The estimation rule has been ${rule.enabled ? 'disabled' : 'enabled'}.`
    //   });
    }
  };

  const handleMovePriority = (ruleId: string, direction: 'up' | 'down') => {
    const ruleIndex = estimationRules.findIndex(rule => rule.id === ruleId);
    if (
      (direction === 'up' && ruleIndex === 0) || 
      (direction === 'down' && ruleIndex === estimationRules.length - 1)
    ) {
      return;
    }

    const newRules = [...estimationRules];
    const swapIndex = direction === 'up' ? ruleIndex - 1 : ruleIndex + 1;
    
    // Swap priority numbers
    const ruleCurrentPriority = newRules[ruleIndex].priority;
    newRules[ruleIndex].priority = newRules[swapIndex].priority;
    newRules[swapIndex].priority = ruleCurrentPriority;
    
    // Sort by priority
    newRules.sort((a, b) => a.priority - b.priority);
    
    setEstimationRules(newRules);
    
    // toast({
    //   title: "Priority Updated",
    //   description: `${newRules[ruleIndex].name} priority changed to ${newRules[ruleIndex].priority}.`
    // });
  };

  const handleSave = () => {
    // toast({
    //   title: "Estimation Rules Saved",
    //   description: "Your estimation rule configurations have been saved."
    // });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] lg:max-w-[700px] max-h-[80vh] overflow-y-auto cursor-not-allowed">
        <DialogHeader>
          <DialogTitle>Estimation Rules</DialogTitle>
          <DialogDescription>
            Enable, disable and set priority for estimation logics.
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-6 space-y-6">
          {estimationRules.map((rule) => (
            <div key={rule.id} className="border rounded-md p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded">
                      Priority: {rule.priority}
                    </span>
                    <h3 className="font-medium">{rule.name}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{rule.description}</p>
                </div>
                <div className="flex flex-col items-center ml-4 space-y-2">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 cursor-not-allowed" 
                    onClick={() => handleMovePriority(rule.id, 'up')}
                    disabled={true}
                  >
                    <ChevronUp className="h-4 w-4" />
                    <span className="sr-only">Move up</span>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 cursor-not-allowed" 
                    onClick={() => handleMovePriority(rule.id, 'down')}
                    disabled={true}
                  >
                    <ChevronDown className="h-4 w-4" />
                    <span className="sr-only">Move down</span>
                  </Button>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <Label htmlFor={`${rule.id}-switch`} className="sr-only">
                    {rule.enabled ? "Enabled" : "Disabled"}
                  </Label>
                  <Switch
                  disabled={true}
                  className='cursor-not-allowed'
                    id={`${rule.id}-switch`} 
                    checked={rule.enabled} 
                    onCheckedChange={() => handleToggle(rule.id)} 
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} className="mr-2">
            Cancel
          </Button>
          <Button onClick={handleSave} className=' cursor-not-allowed'>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};